import { useCallback, useState } from "react"

export type InvitationProjectValues = {
  // BigInt project id as a string (codegen shape); null lets the team's
  // project set decide which project the invitees start in.
  ProjectId: string | null
  // Whether a team with no projects gets one created for the invitees.
  AutoCreateProject: boolean
  // Country of a project created for the team; null takes the team's own
  // default country, then Norway. Never applies to an existing project.
  CountryCode: number | null
}

/**
 * Holds the default-project choice for an invite batch, shared by the global
 * and the team-scoped invite dialogs so the two can't drift. Every field is
 * an override on top of the backend's rules; `values` is what goes on the
 * wire.
 */
export function useInvitationProjectValues() {
  const [projectId, setProjectId] = useState<string | null>(null)
  const [autoCreateProject, setAutoCreateProject] = useState(true)
  const [countryCode, setCountryCode] = useState<number | null>(null)

  // Every choice here is scoped to one team — its projects, its country —
  // so switching teams has to drop all of them together, not just the one
  // the caller remembers.
  const reset = useCallback(() => {
    setProjectId(null)
    setAutoCreateProject(true)
    setCountryCode(null)
  }, [])

  const values: InvitationProjectValues = {
    ProjectId: projectId,
    AutoCreateProject: autoCreateProject,
    CountryCode: countryCode,
  }

  return {
    projectId,
    setProjectId,
    autoCreateProject,
    setAutoCreateProject,
    countryCode,
    setCountryCode,
    reset,
    values,
  }
}
